/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from "react";
import { motion } from "motion/react";
import { BRAND_IMAGES } from "../data";
import { useLanguage } from "../context/LanguageContext";
import { getOptimizedImageUrl } from "../utils/image";

export default function Hero() {
  const { isRtl, t } = useLanguage();

  return (
    <section className="relative flex flex-col items-center justify-center overflow-hidden border-b border-[#d4af37]/30 stone-texture px-4 pt-12 pb-16 md:pt-16 md:pb-24">
      {/* Imposing Atmospheric Background Image with Parallax & Dark Gradients */}
      <div className="absolute inset-0 z-0">
        <img
          src={getOptimizedImageUrl(BRAND_IMAGES.heroBg, 1000, 75)}
          alt="Ancient Persian Empire at twilight"
          className="w-full h-full object-cover object-center opacity-70 scale-105 filter blur-[0.3px]"
          loading="eager"
          fetchPriority="high"
        />
        {/* Obsidian and Royal Gold Radial Overlay - Adjusted to make background brighter & more visible */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-[#121212]/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#121212]/50 via-transparent to-[#121212]/50" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.15)_0%,transparent_70%)]" />
      </div>

      <div className="relative z-20 max-w-4xl w-full mx-auto flex flex-col items-center">
        {/* Brand Royal Seal with Spinning/Pulse Animation */}
        <motion.div
          initial={{ opacity: 0, scale: 1.4 }}
          animate={{ opacity: 1, scale: 1.4 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="relative mb-8 cursor-pointer group"
        >
          {/* Unified golden-amber glow without any dark/blackish centers */}
          <div className="absolute inset-0 rounded-full bg-amber-500/30 blur-xl group-hover:bg-amber-400/50 transition-all duration-700 animate-pulse" />
          
          {/* Transparent Wrapper that lets the logo fit perfectly */}
          <div className="relative w-36 h-36 md:w-40 md:h-40 flex items-center justify-center">
            <img
              src={getOptimizedImageUrl(BRAND_IMAGES.brandSeal, 200)}
              alt="RazoRavan Logo"
              className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-110 drop-shadow-[0_0_15px_rgba(212,175,55,0.4)]"
              loading="eager"
              fetchPriority="high"
            />
          </div>
        </motion.div>

        {/* Dynamic Bold Titles */}
        <div className="text-center max-w-3xl px-4 select-none flex flex-col items-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-4xl md:text-5xl font-extrabold tracking-tight gold-text font-sans drop-shadow-[0_2px_12px_rgba(0,0,0,0.9)] pb-2"
          >
            {t("heroTitle")}
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="text-lg md:text-xl text-amber-100/90 font-medium font-sans mt-3 tracking-wide text-center"
          >
            {t("heroSubtitle")}
          </motion.p>

          {/* Gold glow line matching Elegant Dark custom theme */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-5 w-48 md:w-64 h-[2px] bg-[#d4af37] shadow-[0_0_12px_rgba(212,175,55,0.8)]"
          />

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="text-sm text-gray-300 max-w-2xl mx-auto leading-relaxed font-sans mt-5 text-center"
          >
            {t("heroDescription")}
          </motion.p>
        </div>
      </div>
    </section>
  );
}
